<template>
    <div class="championship-playoff">
        <div v-for="stageGroup in playoffGames" :key="stageGroup.stage" class="playoff-stage">
            <!-- <h2 class="stage-title">{{ stageGroup.stage_name }}</h2> -->

            <!-- Группируем матчи по парам команд для серий -->
            <div class="playoff-series">
                <div v-for="series in getSeriesForStage(stageGroup.games)" :key="series.key" class="series">
                    <div class="series-teams">
                        <component
                            :is="teamPublicPath(series.homeTeam) ? 'a' : 'div'"
                            :href="teamPublicPath(series.homeTeam) || undefined"
                            class="series-team"
                            :class="{
                                'series-winner': series.homeWins > series.awayWins,
                                'series-team--link': !!teamPublicPath(series.homeTeam),
                            }"
                        >
                            <div class="series-team-logo" v-if="series.homeTeam.logo_path">
                                <img :src="series.homeTeam.logo_path" :alt="series.homeTeam.name" />
                            </div>
                            <div class="series-team-logo placeholder" v-else>
                                {{ series.homeTeam.name?.charAt(0) }}
                            </div>
                            <span class="series-team-name">{{ series.homeTeam.name }}</span>
                            <span v-if="series.totalGames > 0" class="series-score">{{ series.homeWins }}</span>
                        </component>
                        <div class="series-vs">VS</div>
                        <component
                            :is="teamPublicPath(series.awayTeam) ? 'a' : 'div'"
                            :href="teamPublicPath(series.awayTeam) || undefined"
                            class="series-team"
                            :class="{
                                'series-winner': series.awayWins > series.homeWins,
                                'series-team--link': !!teamPublicPath(series.awayTeam),
                            }"
                        >
                            <div class="series-team-logo" v-if="series.awayTeam.logo_path">
                                <img :src="series.awayTeam.logo_path" :alt="series.awayTeam.name" />
                            </div>
                            <div class="series-team-logo placeholder" v-else>
                                {{ series.awayTeam.name?.charAt(0) }}
                            </div>
                            <span class="series-team-name">{{ series.awayTeam.name }}</span>
                            <span v-if="series.totalGames > 0" class="series-score">{{ series.awayWins }}</span>
                        </component>
                    </div>

                    <div class="series-matches">
                        <div
                            v-for="game in series.games"
                            :key="game.id"
                            class="series-match"
                            :class="{ 'match-finished': game.is_finished }"
                        >
                            <div class="match-info">
                                <span class="match-date">{{ game.datetime }}</span>
                                <span v-if="game.venue" class="match-venue">{{ game.venue }}</span>
                            </div>
                            <div class="match-teams">
                                <component
                                    :is="teamPublicPath(game.home_team) ? 'a' : 'div'"
                                    :href="teamPublicPath(game.home_team) || undefined"
                                    class="match-team"
                                    :class="{ 'match-team--link': !!teamPublicPath(game.home_team) }"
                                >
                                    <div class="match-team-logo" v-if="game.home_team?.logo_path">
                                        <img :src="game.home_team.logo_path" :alt="game.home_team.name" />
                                    </div>
                                    <div class="match-team-logo placeholder" v-else>
                                        {{ game.home_team.name?.charAt(0) }}
                                    </div>
                                    <span class="team-name">{{ game.home_team.name }}</span>
                                    <span v-if="game.is_finished" class="team-score">{{ game.home_score }}</span>
                                </component>
                                <span class="match-separator">:</span>
                                <component
                                    :is="teamPublicPath(game.away_team) ? 'a' : 'div'"
                                    :href="teamPublicPath(game.away_team) || undefined"
                                    class="match-team"
                                    :class="{ 'match-team--link': !!teamPublicPath(game.away_team) }"
                                >
                                    <span v-if="game.is_finished" class="team-score">{{ game.away_score }}</span>
                                    <span class="team-name">{{ game.away_team.name }}</span>
                                    <div class="match-team-logo" v-if="game.away_team?.logo_path">
                                        <img :src="game.away_team.logo_path" :alt="game.away_team.name" />
                                    </div>
                                    <div class="match-team-logo placeholder" v-else>
                                        {{ game.away_team.name?.charAt(0) }}
                                    </div>
                                </component>
                            </div>
                            <div
                                v-if="game.sets_score && Array.isArray(game.sets_score) && game.sets_score.length > 0"
                                class="match-sets"
                            >
                                <span class="sets-label">Сеты:</span>
                                <span class="sets-value">{{ formatSetsScore(game.sets_score) }}</span>
                            </div>
                            <div
                                v-if="game.is_finished"
                                class="match-actions"
                            >
                                <a
                                    v-if="game.match_video_url"
                                    :href="game.match_video_url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="match-video-link"
                                >
                                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                        <path
                                            d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm4 8l-5-3v6l5-3z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                    Видео
                                </a>
                                <a
                                    v-if="game.has_stats"
                                    :href="gamePublicPath(game)"
                                    class="match-stats-link"
                                >
                                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                        <path
                                            d="M1 14h2V6H1v8zm4 0h2V2H5v12zm4 0h2V8H9v6zm4 0h2V4h-2v10z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                    Статистика матча
                                </a>
                                <span
                                    v-else
                                    class="match-stats-unavailable"
                                    aria-disabled="true"
                                >
                                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                        <path
                                            d="M1 14h2V6H1v8zm4 0h2V2H5v12zm4 0h2V8H9v6zm4 0h2V4h-2v10z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                    Статистика недоступна
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { formatSetsScore } from '@/utils/matchHelpers';
import { gamePublicPath } from '@/utils/gameUrl';
import { teamPublicPath } from '@/utils/teamUrl';

defineProps({
    playoffGames: {
        type: Array,
        default: () => []
    }
});

// Группируем матчи по парам команд для серий
const getSeriesForStage = (games) => {
    if (!games || games.length === 0) return [];

    // Группируем матчи по парам команд
    const seriesMap = new Map();

    games.forEach(game => {
        const homeId = game.home_team?.id;
        const awayId = game.away_team?.id;

        if (!homeId || !awayId) return;

        // Создаем ключ для серии (упорядоченный по ID команд)
        const seriesKey = `${Math.min(homeId, awayId)}-${Math.max(homeId, awayId)}`;

        if (!seriesMap.has(seriesKey)) {
            const isHomeFirst = homeId < awayId;
            seriesMap.set(seriesKey, {
                key: seriesKey,
                homeTeam: isHomeFirst ? game.home_team : game.away_team,
                awayTeam: isHomeFirst ? game.away_team : game.home_team,
                games: [],
                homeWins: 0,
                awayWins: 0,
                totalGames: 0
            });
        }

        const series = seriesMap.get(seriesKey);
        const isHomeFirst = homeId < awayId;

        // Добавляем матч в серию
        series.games.push(game);
        series.totalGames++;

        // Подсчитываем победы
        if (game.is_finished) {
            if (isHomeFirst) {
                if (game.home_score > game.away_score) {
                    series.homeWins++;
                } else if (game.away_score > game.home_score) {
                    series.awayWins++;
                }
            } else {
                if (game.away_score > game.home_score) {
                    series.homeWins++;
                } else if (game.home_score > game.away_score) {
                    series.awayWins++;
                }
            }
        }
    });

    // Сортируем матчи в каждой серии по дате
    seriesMap.forEach(series => {
        series.games.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateA - dateB;
        });
    });

    return Array.from(seriesMap.values());
};
</script>
